import { useCallback, useMemo } from 'react';

import { ICombo, IModifierSelection, IPicker } from '@rbi-ctg/menu';
import { FIRST_INDEX } from 'components/product-detail-v2/product-detail-flow/product-picker-flow/constants';
import { findCheapestPickerOption } from 'components/product-detail-v2/product-detail-flow/product-picker-flow/utils';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from 'components/product-detail-v2/utils';
import { useRedeemReward } from 'pages/cart/cart-item/hooks/use-redeem-reward-state';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard-v2/types';
import { useUIContext } from 'state/ui';
import { isPicker } from 'utils/menu';
import { IWithVendorConfig } from 'utils/vendor-config';

import { computeAvailableRewardSlots, getBaseQuantityForPricing } from './price-quantity';
import { computeDiscountPrice, getOfferVendorConfigForPricing } from './utils';

export interface ProductPriceConditions {
  shouldIncludeModifications: boolean;
  shouldIncludeQuantity: boolean;
}

export const useProductPrice = (conditions?: ProductPriceConditions) => {
  const { isOffer, pricingFunction } = useMenuContext();
  const {
    menuObject,
    productQuantity,
    selectedProduct,
    userSelections,
  } = useProductWizardContext();
  const { formatCurrencyForLocale } = useUIContext();

  const { incentiveNotInMenu } = useRedeemReward();
  const { rewardBenefitId, cartIdEditing, isFromCart, currentStep } = useProductWizardFlow();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);

  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();

  const selectedOffer = selectedLoyaltyOffer;

  const { shouldIncludeModifications = false, shouldIncludeQuantity = false } = conditions || {};

  // Reward associated with the product currently on screen (may live on either the
  // selected product or the parent menu object). Looked up here so both the slot memo and
  // any future readers stay aligned with what `computeDiscountPrice` resolves internally.
  const incomingReward =
    availableLoyaltyRewardsMap[selectedProduct?._id ?? ''] ||
    availableLoyaltyRewardsMap[menuObject?._id ?? ''];

  // Slots used to scale the base price preview (`getBaseQuantityForPricing`).
  // Computed via the same helper `computeDiscountPrice` calls so the base cap and
  // discount cap stay in lock-step (including the same eviction precedence).
  const availableRewardSlots = useMemo(
    () =>
      computeAvailableRewardSlots({
        appliedLoyaltyRewards,
        rewardRedemptionLimit,
        cartIdEditing,
        rewardBenefitId,
        preventRewardStacking: enableRewardUnstacking,
        incomingRewardId: incomingReward?.id,
        incomingLimitPerOrder: incomingReward?.limitPerOrder,
      }).availableRewardSlots,
    [
      appliedLoyaltyRewards,
      cartIdEditing,
      rewardBenefitId,
      rewardRedemptionLimit,
      enableRewardUnstacking,
      incomingReward?.id,
      incomingReward?.limitPerOrder,
    ]
  );

  const discountPrice = useCallback(() => {
    return computeDiscountPrice({
      appliedLoyaltyRewards,
      availableLoyaltyRewardsMap,
      cartIdEditing,
      pricingFunction,
      productQuantity,
      rewardBenefitId,
      selectedProduct,
      menuObject,
      stagedCartPoints,
      isFromCart,
      preventRewardStacking: enableRewardUnstacking,
      rewardRedemptionLimit,
    });
  }, [
    appliedLoyaltyRewards,
    availableLoyaltyRewardsMap,
    cartIdEditing,
    pricingFunction,
    productQuantity,
    rewardBenefitId,
    selectedProduct,
    menuObject,
    stagedCartPoints,
    isFromCart,
    enableRewardUnstacking,
    rewardRedemptionLimit,
  ]);

  // Number of items the price formula iterates over. Shared by `totalPrice` and the
  // picker landing-page preview so all three branches (base, discount, picker) read
  // from the same slot accounting and can never collapse to $0 due to a base/discount
  // mismatch (see price-quantity.ts for the invariant).
  const baseQuantityForPricing = useMemo(
    () =>
      getBaseQuantityForPricing({
        shouldIncludeQuantity,
        productQuantity,
        isReward: !!rewardBenefitId,
        enableRewardUnstacking,
        availableRewardSlots,
      }),
    [
      shouldIncludeQuantity,
      productQuantity,
      rewardBenefitId,
      enableRewardUnstacking,
      availableRewardSlots,
    ]
  );

  const minPriceForPicker = useCallback(() => {
    const pickerData = menuObject as IPicker;
    const firstPickerAspect = pickerData.pickerAspects[FIRST_INDEX];
    const userSelectionsForFirstPickerAspect =
      userSelections.pickerAspects?.[firstPickerAspect._id];

    const price = findCheapestPickerOption({
      pickerData,
      pickerAspectOptionIdentifier: userSelectionsForFirstPickerAspect || '',
      pricingFunction,
    });

    // For reward pickers, the discount can cover up to `availableRewardSlots` items, so
    // the base must scale to `slots + 1` to keep `Math.max(base − discount, 0)` from
    // collapsing to $0 once productQuantity exceeds the cap.
    const priceWithDiscount = Math.max(price * baseQuantityForPricing - discountPrice(), 0);
    return priceWithDiscount;
  }, [
    discountPrice,
    menuObject,
    pricingFunction,
    baseQuantityForPricing,
    userSelections.pickerAspects,
  ]);

  const totalPrice = useCallback(
    (modifiersInjected?: IModifierSelection[]) => {
      const quantity = baseQuantityForPricing;

      const baseTotal = pricingFunction({ item: selectedProduct, quantity });

      let total = baseTotal;
      if (isOffer) {
        const vendorConfigs = getOfferVendorConfigForPricing(selectedOffer);
        const price = vendorConfigs
          ? pricingFunction({
              // TODO: add _type
              item: { vendorConfigs } as IWithVendorConfig,
              quantity,
              modifierSelections: modifiersInjected,
            })
          : 0;
        total = price || selectedOffer?.offerPrice || 0;
      }

      if (shouldIncludeModifications) {
        const comboSlotSelections = transformUserSelectionsComboToComboSlotSelections(
          userSelections.comboSlot,
          selectedProduct as ICombo
        );
        const modifierSelections = transformUserSelectionsModifierToModifierSelection(
          userSelections
        );
        const extraTotal =
          pricingFunction({
            item: selectedProduct,
            quantity,
            modifierSelections,
            comboSlotSelections,
          }) - baseTotal;

        total += extraTotal;
      }
      return total;
    },
    [
      baseQuantityForPricing,
      isOffer,
      pricingFunction,
      selectedOffer,
      selectedProduct,
      shouldIncludeModifications,
      userSelections,
    ]
  );

  const displayedProductPrice = useMemo(() => {
    // On the Product Home page, we want to show the cheapest price for the picker.
    if (isPicker(menuObject) && currentStep === ProductWizardStep.ProductHome) {
      if (isOffer && selectedOffer) {
        return selectedOffer.offerPrice || 0;
      }

      return minPriceForPicker();
    }

    return incentiveNotInMenu ? 0 : Math.max(totalPrice() - discountPrice(), 0);
  }, [
    menuObject,
    currentStep,
    incentiveNotInMenu,
    totalPrice,
    discountPrice,
    isOffer,
    selectedOffer,
    minPriceForPicker,
  ]);

  const formattedProductPrice = useMemo(() => formatCurrencyForLocale(displayedProductPrice), [
    displayedProductPrice,
    formatCurrencyForLocale,
  ]);

  return {
    totalPrice,
    displayedProductPrice,
    formattedProductPrice,
  };
};
